import Dexie, { type Table } from 'dexie'
import type { DateRange, HealthDataProvider } from '../../domain/health/contracts'
import type { MetricType } from '../../domain/health/types'
import { parseShortcutSnapshot, readLimitedJson, type ShortcutSnapshot } from '../../domain/health/shortcutSync'

class SyncDb extends Dexie {
  state!: Table<{ key: string; token?: string; snapshot?: ShortcutSnapshot; checkedAt?: string }, string>
  constructor(name: string) {
    super(name)
    this.version(1).stores({ state: 'key' })
  }
}
const EMPTY = { metrics: [], sleeps: [], workouts: [] }
export class ShortcutSyncProvider implements HealthDataProvider {
  readonly source = 'shortcuts' as const
  private readonly db: SyncDb
  private active: Promise<void> | null = null
  private generation = 0
  private closed = false
  private controller: AbortController | null = null
  private listeners = new Set<() => void>()
  status = '未连接快捷指令同步'
  enabled = false
  constructor(name = 'carebaby-shortcut-sync', private readonly request: typeof fetch = (input, init) => fetch(input, init)) {
    this.db = new SyncDb(name)
  }
  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener) } }
  private notify() { for (const listener of this.listeners) listener() }
  async connection() {
    const state = await this.db.state.get('connection').catch((error) => {
      if (!this.closed) throw error
      return undefined
    })
    return { connected: Boolean(state?.token), checkedAt: state?.checkedAt }
  }
  async requestFoodLookup(body: import('../../domain/health/boohee').BooheeRequest, signal: AbortSignal): Promise<Response> {
    const state = await this.db.state.get('connection')
    if (!state?.token) throw new Error('请先在设置中连接同步读取密钥，以使用私人食物查询')
    return this.request('/api/food-lookup', {
      method: 'POST', body: JSON.stringify(body), signal,
      headers: { Authorization: `Bearer ${state.token}`, 'Content-Type': 'application/json' },
      cache: 'no-store', credentials: 'omit', redirect: 'error', referrerPolicy: 'no-referrer',
    })
  }
  async requestFoodPhoto(image: string, signal: AbortSignal): Promise<Response> {
    const state = await this.db.state.get('connection')
    if (!state?.token) throw new Error('请先在设置中连接同步读取密钥，以启用私人拍照入口')
    return this.request('/api/food-photo', {
      method: 'POST', body: JSON.stringify({ image }), signal,
      headers: { Authorization: `Bearer ${state.token}`, 'Content-Type': 'application/json' },
      cache: 'no-store', credentials: 'omit', redirect: 'error', referrerPolicy: 'no-referrer',
    })
  }
  async connect(token: string) {
    if (!/^[A-Za-z0-9_-]{32,256}$/.test(token)) throw new Error('请输入有效的读取密钥')
    // Abort old-account requests before switching credentials and dropping its cache.
    this.generation += 1
    this.controller?.abort()
    await this.db.transaction('rw', this.db.state, async () => {
      await this.db.state.clear()
      await this.db.state.put({ key: 'connection', token })
    })
    if (this.active) await this.active
    await this.sync()
  }
  async disconnect() {
    this.generation += 1
    this.controller?.abort()
    await this.db.state.clear()
    this.status = '已断开同步并清除本机同步缓存；云端数据仍保留'
    this.enabled = false
    this.notify()
  }
  sync(): Promise<void> {
    if (this.active) return this.active
    if (this.closed) return Promise.resolve()
    this.active = this.performSync().catch(() => {
      if (!this.closed) { this.status = '本机同步缓存不可用，请重试'; this.notify() }
    }).finally(() => { this.active = null })
    return this.active
  }
  private async performSync() {
    const generation = this.generation
    const state = await this.db.state.get('connection')
    if (!state?.token || generation !== this.generation) return
    this.enabled = true
    this.controller = new AbortController()
    const timer = setTimeout(() => this.controller?.abort(), 10_000)
    this.status = '正在同步…'
    this.notify()
    try {
      const response = await this.request('/api/sync', {
        headers: { Authorization: `Bearer ${state.token}` }, cache: 'no-store',
        credentials: 'omit', redirect: 'error', signal: this.controller.signal,
      })
      if (response.status === 401) throw new Error('读取密钥无效，请重新连接')
      if (response.status === 404) throw new Error('尚未收到快捷指令上传的数据')
      if (!response.ok) throw new Error('同步服务暂不可用；已保留上次数据')
      const { snapshot } = parseShortcutSnapshot(await readLimitedJson(response.body, 512 * 1024))
      if (generation !== this.generation) return
      await this.db.transaction('rw', this.db.state, async () => {
        if (generation !== this.generation) return
        const previous = await this.db.state.get('snapshot')
        if (!previous?.snapshot || previous.snapshot.exportedAt <= snapshot.exportedAt) {
          await this.db.state.put({ key: 'snapshot', snapshot })
        }
        await this.db.state.put({ ...state, checkedAt: new Date().toISOString() })
      })
      if (generation !== this.generation) return
      this.status = `已同步 · 数据导出于 ${new Date(snapshot.exportedAt).toLocaleString()}`
    } catch (error) {
      if (generation !== this.generation) return
      this.status = error instanceof Error && error.name !== 'AbortError'
        ? error.message : '网络不可用或超时；已保留上次数据'
    } finally {
      clearTimeout(timer)
      if (generation === this.generation) this.notify()
    }
  }
  private async records() {
    const row = await this.db.state.get('snapshot')
    return row?.snapshot ? parseShortcutSnapshot(row.snapshot).records : EMPTY
  }
  async listMetricHistory(type: MetricType) { return (await this.records()).metrics.filter((r) => r.type === type) }
  async listSleepHistory() { return (await this.records()).sleeps }
  async listWorkoutHistory() { return (await this.records()).workouts }
  async listMetrics(range: DateRange) { return (await this.records()).metrics.filter((r) => r.recordedAt >= range.startInclusive && r.recordedAt < range.endExclusive) }
  async listSleeps(range: DateRange) { return (await this.records()).sleeps.filter((r) => r.endAt >= range.startInclusive && r.endAt < range.endExclusive) }
  async listWorkouts(range: DateRange) { return (await this.records()).workouts.filter((r) => r.startedAt >= range.startInclusive && r.startedAt < range.endExclusive) }
  async latestMetric(type: MetricType, before: string) {
    return (await this.listMetricHistory(type)).filter((r) => r.recordedAt < before)
      .sort((a, b) => b.recordedAt.localeCompare(a.recordedAt) || b.id.localeCompare(a.id))[0] ?? null
  }
  close() { this.closed = true; this.generation += 1; this.controller?.abort(); this.db.close() }
}
